import {AxiosError} from 'axios';
import React, {useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {Box, Button, ErrorInfo, Grid, Typography} from 'web-library';
import {
  GroupRegistrationSummaryResponse
} from '../../../../api-refactored/group-exam-registration/useGetRegistrationSummary';
import {
  usePostUploadPresignedUrl
} from '../../../../api-refactored/group-exam-registration/usePostUploadPresignedUrl';
import {
  PutStartFileProcessingRequest
} from '../../../../api-refactored/group-exam-registration/usePutStartFileProcessing';
import {
  usePutStopFileProcessing
} from '../../../../api-refactored/group-exam-registration/usePutStopFileProcessing';
import {usePutUploadToS3} from '../../../../api-refactored/s3-upload/usePutUploadFileToS3';
import {GenericErrorResponse} from '../../../../api-refactored/typings';
import {UseCustomMutationReturn} from '../../../../api-refactored/utils/useCustomMutation';
import {UseCustomQueryReturn} from '../../../../api-refactored/utils/useCustomQuery';
import {
  useGetDocumentUploadStatus
} from '../../../../api-refactored/document/useGetDocumentUploadStatus';
import {FileProcessingState, FileUploadState} from '../../../../models/File';
import DocumentUploader from './DocumentUploader';
import {ComponentFileProcessingState, IStylingAndTranslationHooks} from './model';
import {
  StartFileProcessingActionLabel,
  StartFileProcessingVisualLabel,
} from './StartFileProcessing';
import {
  StartFileProcessingErrorActionLabel,
  StartFileProcessingErrorVisualLabel,
} from './StartFileProcessingError';
import {
  StartFileProcessingSuccessActionLabel,
  StartFileProcessingSuccessVisualLabel,
} from './StartFileProcessingSuccess';
import {UploadingInProgressVisualLabel} from './UploadingInProgress';
import UploadStateAndAction
  from '../../../../components/upload-processing-file/UploadStateAndAction';
import RouteKey from '../../../../routes/key';
import {uploadFile} from '../../../../utils/FileUtils';

interface IUploadSection extends IStylingAndTranslationHooks {
  apiHooks: {
    getGroupRegistrationSummaryQuery: UseCustomQueryReturn<
        GroupRegistrationSummaryResponse,
        AxiosError<GenericErrorResponse>
    >;
    startFileProcessingMutation: UseCustomMutationReturn<
        void,
        AxiosError<GenericErrorResponse>,
        PutStartFileProcessingRequest,
        unknown
    >;
  };
  stateHandler: {
    setIsFileProcessedHandler: {
      isFileProcessedSuccess: ComponentFileProcessingState;
      setIsFileProcessedSuccess: React.Dispatch<React.SetStateAction<ComponentFileProcessingState>>;
    };
  };
  isFileProcessedSuccess: ComponentFileProcessingState;
  token?: string;
}

const {exam} = RouteKey.child;

const {
  coordinatorExamRegistrations,
  coordinatorRegisterExam,
  coordinatorReviewCandidates,
} = RouteKey.exam.child;

const UploadSection = ({
                         classes,
                         translation: t,
                         stateHandler,
                         apiHooks,
                         isFileProcessedSuccess,
                         token,
                       }: IUploadSection) => {
  const {uuid: applicationUuid} = useParams<any>();
  const {getGroupRegistrationSummaryQuery, startFileProcessingMutation} = apiHooks;
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState<string>('');

  const uploadMutation = usePutUploadToS3();
  const history = useHistory();
  const getUploadPresignedUrlMutation = usePostUploadPresignedUrl({
    onSuccess: (res) => {
      setUploadFileErrorMessage('');
      uploadFile(res, uploadedFiles, uploadMutation.mutate);
    },
    onError: (err) => setUploadFileErrorMessage(err?.response?.data.message!),
  });
  const fileUuid = getUploadPresignedUrlMutation.data?.key;

  const getDocumentUploadStatus = useGetDocumentUploadStatus({
    fileKey: fileUuid || '',
    enabled: !!fileUuid && startFileProcessingMutation.isIdle,
    refetchInterval: 5000,
  });

  const resetUploadAndProcessingApi = () => {
    startFileProcessingMutation.reset();
    getUploadPresignedUrlMutation.reset();
    getDocumentUploadStatus.remove();
    getGroupRegistrationSummaryQuery.remove();
  };
  const stopFileProcessingMutation = usePutStopFileProcessing({
    onSuccess: () => {
      resetUploadAndProcessingApi();
    },
  });
  const fileStatus = getDocumentUploadStatus.data?.fileStatus;
  const fileProcessingStatus = getGroupRegistrationSummaryQuery.data?.fileStatus;

  const startFileProcessingMutationFn = startFileProcessingMutation.mutate;
  useEffect(() => {
    if (fileStatus === FileUploadState.SUCCESS && fileUuid) {
      startFileProcessingMutationFn({fileUuid, applicationUuid});
    }
  }, [fileStatus, fileUuid, applicationUuid, startFileProcessingMutationFn]);

  const IsUploading = getDocumentUploadStatus.data?.fileStatus === FileUploadState.UPLOADING;
  const isUploadFailed = getDocumentUploadStatus.data?.fileStatus === FileUploadState.FAILED;

  const IsProcessingInProgress = fileProcessingStatus === FileProcessingState.PROCESSING;

  const IsProcessingError = isFileProcessedSuccess === ComponentFileProcessingState.ERROR;

  const conditionToShowDocumentUploader =
      (fileProcessingStatus === null && uploadMutation.isIdle) || IsProcessingError || isUploadFailed;

  const IsProcessingSuccess = fileProcessingStatus === FileProcessingState.SUCCESS;

  const disabledProceedButtonCondition = fileProcessingStatus !== FileProcessingState.SUCCESS;

  const disabledBackButtonCondition = IsUploading || IsProcessingInProgress;

  return (
      <>
        <Typography variant="h3" className={classes.header}>
          {t('exam->>UploadExcelTemplate.UploadDocumentsHeader')}
        </Typography>
        {IsProcessingError && (
            <Box className={classes.errorProcessingContainer}>
              <UploadStateAndAction
                  classes={classes}
                  strings={{
                    instruction: t('exam->>UploadExcelTemplate.StartFileProcessingErrorInstruction'),
                  }}
                  labels={{
                    visualLabel: (
                        <StartFileProcessingErrorVisualLabel classes={classes} translation={t}/>
                    ),
                    actionLabel: (
                        <StartFileProcessingErrorActionLabel classes={classes} translation={t}/>
                    ),
                  }}
              />
            </Box>
        )}

        {conditionToShowDocumentUploader && (
            <DocumentUploader
                key={getDocumentUploadStatus.data?.fileStatus}
                fileStatus={getDocumentUploadStatus.data?.fileStatus}
                classes={classes}
                translation={t}
                stateHandler={{
                  setUploadedFileHandler: {uploadedFiles, setUploadedFiles},
                  setIsFileProcessedHandler: stateHandler.setIsFileProcessedHandler,
                }}
                apiHooks={{
                  uploadMutation,
                  startFileProcessingMutation,
                  stopFileProcessingMutation,
                  getUploadPresignedUrlMutation,
                }}
                showUploadInstruction={!IsProcessingError}
            />
        )}

        {IsUploading && (
            <UploadStateAndAction
                classes={classes}
                strings={{
                  htmlInstruction: 'exam->>UploadExcelTemplate.UploadingInProgressInstruction',
                }}
                labels={{
                  visualLabel: <UploadingInProgressVisualLabel classes={classes} translation={t}/>,
                }}
            />
        )}
        {IsProcessingInProgress && (
            <UploadStateAndAction
                classes={classes}
                strings={{
                  instruction: t('exam->>UploadExcelTemplate.ProcessingInProgressInstruction'),
                }}
                labels={{
                  visualLabel: <StartFileProcessingVisualLabel classes={classes} translation={t}/>,
                  actionLabel: (
                      <StartFileProcessingActionLabel
                          classes={classes}
                          translation={t}
                          apiHooks={{
                            stopFileProcessingMutation,
                            getGroupRegistrationSummaryQuery,
                            getDocumentUploadStatus,
                          }}
                          applicationUuid={applicationUuid}
                      />
                  ),
                }}
            />
        )}

        {IsProcessingSuccess && (
            <UploadStateAndAction
                classes={classes}
                strings={{
                  instruction: t('exam->>UploadExcelTemplate.UploadSuccessInstruction'),
                }}
                labels={{
                  visualLabel: (
                      <StartFileProcessingSuccessVisualLabel classes={classes} translation={t}/>
                  ),
                  actionLabel: (
                      <StartFileProcessingSuccessActionLabel
                          classes={classes}
                          translation={t}
                          resetUploadAndProcessingApi={resetUploadAndProcessingApi}
                      />
                  ),
                }}
            />
        )}

        <ErrorInfo label={uploadFileErrorMessage}/>

        <Grid
            className={classes.buttonRow}
            container
            direction="row"
            alignItems="flex-start"
            spacing={3}
        >
          <Grid xs={12} md={3}>
            <Button
                id="button-bulkUpload-back-to-instruction"
                fullWidth
                disabled={disabledBackButtonCondition}
                className={classes.button}
                variant="outlined"
                onClick={() => {
                  let path = `/${exam}/${coordinatorExamRegistrations.route}/${coordinatorRegisterExam.route}/${getGroupRegistrationSummaryQuery.data?.examType.code}`;

                  if (token) {
                    path += `?token=${token}`;
                  }

                  history.push(path);
                }}
            >
              {t('exam->>UploadExcelTemplate.Back')}
            </Button>
          </Grid>
          <Grid xs={12} md={4}>
            <Button
                id="button-bulkUpload-proceed-to-viewCandidateList"
                fullWidth
                className={classes.button}
                disabled={disabledProceedButtonCondition}
                onClick={() =>
                    history.push(
                        `/${exam}/${coordinatorExamRegistrations.route}/${applicationUuid}/${coordinatorReviewCandidates.route}`,
                        {token}
                    )
                }
            >
              {t('exam->>UploadExcelTemplate.ViewCandidateList')}
            </Button>
          </Grid>
        </Grid>
      </>
  );
};

export default UploadSection;
